import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components } from '@/api/types/api-gateway';

// S14 — Groups screen queries. Backed by R3 endpoints.

export type Group = components['schemas']['Group'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useGroups(clients: ApiClients) {
  return useQuery({
    queryKey: ['groups', 'list'],
    queryFn: async (): Promise<Group[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/groups');
      const body = unwrap(data, error, 'groups');
      return body.items;
    },
    staleTime: 30_000,
  });
}

export function useCreateGroup(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; description?: string; scope?: string }) => {
      const { data, error } = await clients.apiGateway.POST('/v1/groups', {
        body: { name: vars.name, description: vars.description, scope: vars.scope },
      });
      if (error) throw new Error('create group failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function usePatchGroup(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      groupId: string;
      name?: string;
      description?: string;
      scope?: string;
    }) => {
      const { data, error } = await clients.apiGateway.PATCH('/v1/groups/{groupId}', {
        params: { path: { groupId: vars.groupId } },
        body: {
          ...(vars.name !== undefined ? { name: vars.name } : {}),
          ...(vars.description !== undefined ? { description: vars.description } : {}),
          ...(vars.scope !== undefined ? { scope: vars.scope } : {}),
        },
      });
      if (error) throw new Error('patch group failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useDeleteGroup(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { groupId: string }) => {
      const { error } = await clients.apiGateway.DELETE('/v1/groups/{groupId}', {
        params: { path: { groupId: vars.groupId } },
      });
      if (error) throw new Error('delete group failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useAddGroupMember(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { groupId: string; userId: string }) => {
      const { data, error } = await clients.apiGateway.POST(
        '/v1/groups/{groupId}/members',
        {
          params: { path: { groupId: vars.groupId } },
          body: { user_id: vars.userId },
        },
      );
      if (error) throw new Error('add member failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useRemoveGroupMember(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { groupId: string; userId: string }) => {
      const { error } = await clients.apiGateway.DELETE(
        '/v1/groups/{groupId}/members/{userId}',
        {
          params: { path: { groupId: vars.groupId, userId: vars.userId } },
        },
      );
      if (error) throw new Error('remove member failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}
